import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  switchLanguage,
  type SupportedLanguage,
} from '@/i18n';

/**
 * Sourced from the i18n config rather than restated here.
 *
 * This alias used to name four languages while the switcher offered two and
 * two of the four were a third translated. One list, in one place, is what
 * stops the cycle below walking somewhere the product does not ship.
 */
type Language = SupportedLanguage;

interface LanguageContextType {
  language: Language;
  isRTL: boolean;
  changeLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  // Derived, not mirrored into state: `useTranslation` re-renders this
  // provider when the language changes, so there is nothing to keep in sync.
  const language: Language = i18n.language?.startsWith('ar') ? 'ar' : 'en';

  const isRTL = language === 'ar';

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [language, isRTL]);

  const changeLanguage = (lang: Language) => {
    void switchLanguage(lang);
  };

  const toggleLanguage = () => {
    const cycle: readonly Language[] = SUPPORTED_LANGUAGES;
    const nextLang = cycle[(cycle.indexOf(language) + 1) % cycle.length];
    changeLanguage(nextLang);
  };

  return (
    <LanguageContext.Provider
      value={{ language, isRTL, changeLanguage, toggleLanguage }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
